import {PropsWithChildren, useEffect, useRef} from 'react';
import {useKeyPress} from 'react-use';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';

import {useAuth} from '~/store/hooks';
import {authSelectors} from '~/store/slices';

const PIN_VERIFICATION_TIMEOUT = 5 * 60 * 1000; // 5 minutes

export function PinProtect({children}: PropsWithChildren<{}>) {
  const {showPinConfirm, onSetShowPinConfirm} = useAuth();
  const navigate = useNavigate();
  const lastPinVerification = useSelector(authSelectors.getLastPinVerification);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useKeyPress(({key}) => {
    if (showPinConfirm && key === 'Escape') {
      // navigate back to home if user presses escape key while the pin pad is open
      onSetShowPinConfirm({showPinConfirm: false});
      navigate('/');
    }
    return true;
  });

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // require pin immediately if no previous verification
    if (!lastPinVerification) {
      onSetShowPinConfirm({showPinConfirm: true});
      return;
    }

    const now = Date.now();
    const timeSinceLastVerification = now - lastPinVerification;

    if (timeSinceLastVerification >= PIN_VERIFICATION_TIMEOUT) {
      // Pin verification has expired, require pin
      onSetShowPinConfirm({showPinConfirm: true});
    } else {
      // Set timeout for when pin verification will expire
      const timeUntilExpiry = PIN_VERIFICATION_TIMEOUT - timeSinceLastVerification;
      timeoutRef.current = setTimeout(() => {
        onSetShowPinConfirm({showPinConfirm: true});
      }, timeUntilExpiry);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [onSetShowPinConfirm, lastPinVerification]);

  if (showPinConfirm) {
    return null;
  }

  return <>{children}</>;
}
